import React, { useEffect, useState } from "react";
import { RefreshCw } from "lucide-react";
import { useNavigate } from "react-router-dom";
import AdminSidebar from "../components/AdminSidebar";
import AdminTopbar from "../components/AdminTopbar";
import API from "../services/api";
import { getStoredAdmin, isAdminSessionValid } from "../utils/adminAuth";

function AdminLayout({ children }) {
  const navigate = useNavigate();
  const [isAllowed, setIsAllowed] = useState(() => isAdminSessionValid());
  const [viewportWidth, setViewportWidth] = useState(() => window.innerWidth);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const isMobile = viewportWidth <= 900;
  const isLaptop = viewportWidth > 900 && viewportWidth <= 1366;

  useEffect(() => {
    if (!isAllowed) {
      navigate("/admin-login", { replace: true });
    }
  }, [isAllowed, navigate]);

  useEffect(() => {
    if (!isAdminSessionValid()) {
      setIsAllowed(false);
      return undefined;
    }

    let active = true;
    API.get("/admin/session")
      .then((res) => {
        if (!active) return;
        const current = getStoredAdmin();
        const merged = {
          ...(current || {}),
          ...(res.data || {}),
          loginAt: current?.loginAt || Date.now(),
        };
        localStorage.setItem("admin", JSON.stringify(merged));
        setIsAllowed(true);
      })
      .catch(() => {
        localStorage.removeItem("admin");
        if (active) setIsAllowed(false);
      });

    return () => {
      active = false;
    };
  }, []);

  useEffect(() => {
    const onResize = () => {
      const width = window.innerWidth;
      const mobile = width <= 900;
      setViewportWidth(width);
      if (!mobile) setSidebarOpen(false);
    };
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  if (!isAllowed) return null;

  return (
    <div style={shell}>
      <AdminSidebar isMobile={isMobile} isLaptop={isLaptop} isOpen={sidebarOpen} onClose={() => setSidebarOpen(false)} />
      {isMobile && sidebarOpen && <button type="button" aria-label="Close admin menu" style={overlay} onClick={() => setSidebarOpen(false)} />}
      <main style={isMobile ? mobileMain : isLaptop ? laptopMain : main}>
        <AdminTopbar isMobile={isMobile} isLaptop={isLaptop} onMenuClick={() => setSidebarOpen(true)} />
        <div style={isMobile ? mobileContent : isLaptop ? laptopContent : content} className="admin-content">{children}</div>
      </main>
      <button type="button" style={isMobile ? mobileRefreshButton : refreshButton} onClick={() => window.location.reload()} title="Refresh current admin page">
        <RefreshCw size={17} />
      </button>
      <style>{responsiveCss}</style>
    </div>
  );
}

const shell = {
  minHeight: "100vh",
  height: "100dvh",
  background: "#f8f9fb",
  overflowX: "hidden",
  overflowY: "hidden"
};

const main = {
  marginLeft: "220px",
  minHeight: "100vh",
  height: "100dvh",
  transition: "margin-left 0.25s ease",
  display: "flex",
  flexDirection: "column",
  overflow: "hidden"
};

const laptopMain = {
  ...main,
  marginLeft: "200px"
};

const mobileMain = {
  marginLeft: 0,
  minHeight: "100vh",
  height: "100dvh",
  width: "100%",
  overflowX: "hidden",
  display: "flex",
  flexDirection: "column",
  overflowY: "hidden"
};

const content = {
  padding: "28px 32px 48px",
  background: "#f1f5f9",
  minHeight: "calc(100vh - 64px)",
  boxSizing: "border-box",
  overflowX: "hidden",
  overflowY: "auto",
  WebkitOverflowScrolling: "touch",
  overscrollBehaviorY: "contain",
  flex: "1 1 auto"
};

const laptopContent = {
  ...content,
  padding: "18px 18px 40px",
  minHeight: "calc(100vh - 58px)"
};

const mobileContent = {
  ...content,
  padding: "calc(10px + env(safe-area-inset-top)) 10px calc(88px + env(safe-area-inset-bottom))",
  minHeight: "calc(100vh - 56px)",
  overflowX: "hidden",
  overflowY: "auto",
  WebkitOverflowScrolling: "touch",
  overscrollBehaviorY: "contain",
  paddingTop: 10
};

const overlay = {
  position: "fixed",
  inset: 0,
  zIndex: 998,
  border: 0,
  background: "rgba(15,23,42,0.45)"
};

const refreshButton = {
  position: "fixed",
  right: "18px",
  bottom: "18px",
  zIndex: 900,
  width: "44px",
  height: "44px",
  border: "1px solid #bfdbfe",
  borderRadius: "999px",
  background: "linear-gradient(135deg,#eff6ff,#ffffff)",
  color: "#2563eb",
  display: "grid",
  placeItems: "center",
  boxShadow: "0 14px 34px rgba(37,99,235,.18)",
  cursor: "pointer"
};

const mobileRefreshButton = {
  ...refreshButton,
  right: "12px",
  bottom: "calc(74px + env(safe-area-inset-bottom))",
  width: "42px",
  height: "42px"
};

const responsiveCss = `
  [data-admin-theme="dark"] body {
    background: #020617 !important;
  }
  [data-admin-theme="dark"] .admin-content {
    background: #020617 !important;
    color: #e5e7eb !important;
  }
  [data-admin-theme="dark"] .admin-content h1,
  [data-admin-theme="dark"] .admin-content h2,
  [data-admin-theme="dark"] .admin-content h3 {
    color: #f8fafc !important;
  }
  .admin-content * { box-sizing: border-box; }
  .admin-content img, .admin-content video, .admin-content canvas { max-width: 100%; }
  .admin-content table { width: 100%; border-collapse: collapse; }
  @media (min-width: 901px) and (max-width: 1366px) {
    .admin-content {
      font-size: 13px;
    }
    .admin-content h1 { font-size: 23px !important; }
    .admin-content h2 { font-size: 19px !important; }
    .admin-content [style*="padding: 28px"],
    .admin-content [style*="padding: 24px"],
    .admin-content [style*="padding: 22px"],
    .admin-content [style*="padding: 20px"] {
      padding: 16px !important;
    }
    .admin-content [style*="gap: 24px"],
    .admin-content [style*="gap: 22px"],
    .admin-content [style*="gap: 20px"] {
      gap: 14px !important;
    }
    .admin-content [style*="repeat(7"] {
      grid-template-columns: repeat(4, minmax(0, 1fr)) !important;
    }
    .admin-content [style*="repeat(6"],
    .admin-content [style*="repeat(5"] {
      grid-template-columns: repeat(3, minmax(0, 1fr)) !important;
    }
    .admin-content [style*="repeat(4"] {
      grid-template-columns: repeat(2, minmax(0, 1fr)) !important;
    }
    .admin-content .admin-dashboard-kpis {
      grid-template-columns: repeat(3, minmax(0, 1fr)) !important;
      gap: 12px !important;
    }
    .admin-content .admin-dashboard-minis {
      grid-template-columns: repeat(3, minmax(0, 1fr)) !important;
      gap: 10px !important;
    }
    .admin-content .admin-outcome-form {
      grid-template-columns: repeat(3, minmax(0, 1fr)) !important;
      gap: 12px !important;
    }
    .admin-content .admin-outcome-form label:nth-child(2),
    .admin-content .admin-outcome-form label:nth-child(3) {
      grid-column: auto !important;
    }
    .admin-content table {
      font-size: 12px;
    }
    .admin-content th,
    .admin-content td {
      padding-left: 10px !important;
      padding-right: 10px !important;
    }
    .admin-content button,
    .admin-content input,
    .admin-content select,
    .admin-content textarea {
      font-size: 13px !important;
    }
  }
  @media (max-width: 900px) {
    html, body { overflow-x: hidden; }
    .admin-content {
      width: 100%;
      max-width: 100vw;
      overflow-x: hidden;
      -webkit-overflow-scrolling: touch;
      font-size: 12px;
    }
    .admin-content h1 { font-size: 21px !important; line-height: 1.15 !important; }
    .admin-content h2 { font-size: 18px !important; line-height: 1.2 !important; }
    .admin-content h3 { font-size: 15px !important; line-height: 1.2 !important; }
    .admin-content p { font-size: 12px !important; }
    .admin-content section,
    .admin-content article,
    .admin-content form,
    .admin-content div {
      max-width: 100%;
    }
    .admin-content [style*="padding: 28px"],
    .admin-content [style*="padding: 24px"],
    .admin-content [style*="padding: 22px"],
    .admin-content [style*="padding: 20px"] {
      padding: 12px !important;
    }
    .admin-content [style*="gap: 24px"],
    .admin-content [style*="gap: 22px"],
    .admin-content [style*="gap: 20px"],
    .admin-content [style*="gap: 18px"] {
      gap: 10px !important;
    }
    .admin-content [style*="repeat(6"],
    .admin-content [style*="repeat(5"],
    .admin-content [style*="repeat(4"] {
      grid-template-columns: repeat(3, minmax(0, 1fr)) !important;
    }
    .admin-content [style*="repeat(3"] {
      grid-template-columns: repeat(3, minmax(0, 1fr)) !important;
    }
    .admin-content [style*="repeat(2"] {
      grid-template-columns: repeat(2, minmax(0, 1fr)) !important;
    }
    .admin-content input,
    .admin-content select,
    .admin-content textarea {
      font-size: 16px !important;
      max-width: 100%;
      min-height: 40px;
    }
    .admin-content button {
      max-width: 100%;
      min-height: 38px;
      font-size: 12px !important;
    }
    .admin-content select,
    .admin-content textarea {
      -webkit-appearance: none;
      appearance: none;
    }
    .admin-content button {
      padding-left: 10px !important;
      padding-right: 10px !important;
      white-space: nowrap;
    }
    .admin-content table {
      display: block;
      width: 100% !important;
      min-width: 0 !important;
      border-collapse: separate;
      border-spacing: 0 6px;
      overflow: visible;
    }
    .admin-content thead {
      display: none;
    }
    .admin-content tbody,
    .admin-content tr,
    .admin-content td {
      display: block;
      width: 100% !important;
    }
    .admin-content tr {
      margin-bottom: 6px;
      padding: 7px 8px;
      background: #fff;
      border: 1px solid #e5e7eb;
      border-radius: 12px;
      box-shadow: 0 3px 10px rgba(15, 23, 42, 0.04);
    }
    .admin-content td {
      border: 0 !important;
      padding: 3px 1px !important;
      font-size: 11px !important;
      white-space: normal !important;
      word-break: break-word;
      line-height: 1.2 !important;
    }
    .admin-content td * {
      max-width: 100%;
      white-space: normal !important;
      word-break: break-word;
    }
    .admin-content td [style*="display: flex"] {
      min-width: 0 !important;
    }
    .admin-content td:empty {
      display: none;
    }
    .admin-content td:nth-child(n+7) {
      display: none;
    }
    .admin-content tr td:first-child {
      font-weight: 900;
      color: #0f172a;
    }
    .admin-content tr td:last-child {
      display: block;
      padding-top: 6px !important;
    }
    .admin-content tr td:last-child > div,
    .admin-content tr td:last-child button {
      display: inline-flex !important;
      flex-wrap: wrap;
      gap: 5px !important;
      align-items: center;
    }
    .admin-content .admin-mobile-full {
      grid-column: 1 / -1 !important;
    }
    .admin-content .admin-mobile-two {
      grid-template-columns: repeat(2, minmax(0, 1fr)) !important;
    }
    .admin-content .admin-mobile-three {
      grid-template-columns: repeat(3, minmax(0, 1fr)) !important;
    }
    .admin-content .admin-mobile-stack {
      grid-template-columns: 1fr !important;
    }
    .admin-content .admin-mobile-hide {
      display: none !important;
    }
    .admin-content .admin-mobile-actions {
      grid-template-columns: repeat(2, minmax(0, 1fr)) !important;
      gap: 8px !important;
    }
    .admin-content .admin-mobile-actions button {
      min-height: 40px !important;
      border-radius: 12px !important;
    }
    .admin-content .admin-starline-form-grid {
      grid-template-columns: 1fr 1fr !important;
      gap: 10px !important;
    }
    .admin-content .admin-starline-slot-grid {
      grid-template-columns: repeat(2, minmax(0, 1fr)) !important;
      gap: 8px !important;
    }
    .admin-content .admin-starline-button-row {
      grid-template-columns: 1fr !important;
      gap: 10px !important;
    }
    .admin-content .admin-market-header {
      display: grid !important;
      grid-template-columns: 1fr !important;
      gap: 10px !important;
      align-items: stretch !important;
    }
    .admin-content .admin-market-header-buttons {
      display: grid !important;
      grid-template-columns: minmax(0, 92px) 1fr !important;
      gap: 8px !important;
      width: 100% !important;
    }
    .admin-content .admin-market-refresh-button,
    .admin-content .admin-market-create-button {
      min-height: 42px !important;
      height: 42px !important;
      border-radius: 12px !important;
      justify-content: center !important;
      display: inline-flex !important;
      align-items: center !important;
      gap: 6px !important;
      white-space: nowrap !important;
    }
    .admin-content .admin-market-stats-grid,
    .admin-content .admin-market-form-grid,
    .admin-content .admin-market-time-grid,
    .admin-content .admin-market-footer-row {
      grid-template-columns: 1fr !important;
    }
    .admin-content .admin-market-day-grid {
      grid-template-columns: repeat(3, minmax(0, 1fr)) !important;
      gap: 8px !important;
    }
    .admin-content .admin-market-exception-row {
      grid-template-columns: 1fr 1fr !important;
      gap: 8px !important;
      padding: 8px !important;
      border: 1px solid #e5e7eb !important;
      border-radius: 12px !important;
      background: #f8fafc !important;
    }
    .admin-content .admin-market-exception-row > *:nth-child(1) {
      grid-column: 1 / -1 !important;
    }
    .admin-content .admin-market-exception-row > *:nth-child(4) {
      grid-column: 1 / 2 !important;
    }
    .admin-content .admin-market-exception-row > *:nth-child(5) {
      grid-column: 2 / 3 !important;
      justify-self: end !important;
    }
    .admin-content .admin-market-modal-body {
      padding: 14px !important;
      overflow-y: auto !important;
      max-height: calc(100vh - 120px) !important;
      -webkit-overflow-scrolling: touch !important;
    }
    .admin-content .admin-market-modal-overlay {
      align-items: start !important;
      padding-top: 10px !important;
      padding-bottom: 10px !important;
    }
    .admin-content .admin-modal-backdrop {
      display: block !important;
      overflow-y: auto !important;
      padding: 10px !important;
      -webkit-overflow-scrolling: touch !important;
    }
    .admin-content .admin-modal-card {
      width: 100% !important;
      max-width: 100% !important;
      max-height: calc(100vh - 20px) !important;
      border-radius: 16px !important;
      overflow: hidden !important;
    }
    .admin-content .admin-modal-body {
      max-height: calc(100vh - 154px) !important;
      overflow-y: auto !important;
      padding: 12px !important;
      -webkit-overflow-scrolling: touch !important;
    }
    .admin-content .admin-outcome-form {
      grid-template-columns: repeat(3, minmax(0, 1fr)) !important;
      gap: 8px !important;
      margin-top: 10px !important;
    }
    .admin-content .admin-outcome-form label {
      gap: 4px !important;
      font-size: 10px !important;
    }
    .admin-content .admin-outcome-form input,
    .admin-content .admin-outcome-form select,
    .admin-content .admin-outcome-form button,
    .admin-content .admin-outcome-form div[style*="58px"] {
      min-height: 40px !important;
      height: 40px !important;
      font-size: 12px !important;
      padding-left: 8px !important;
      padding-right: 8px !important;
    }
    .admin-content .admin-outcome-form label:nth-child(2) {
      grid-column: 1 / -1 !important;
    }
    .admin-content .admin-outcome-selected {
      padding: 8px 10px !important;
      margin-top: 10px !important;
      margin-bottom: 4px !important;
      border-radius: 12px !important;
      gap: 8px !important;
    }
    .admin-content .admin-dashboard-kpis {
      display: grid !important;
      grid-template-columns: repeat(3, minmax(0, 1fr)) !important;
      gap: 8px !important;
    }
    .admin-content .admin-dashboard-kpis article {
      min-height: 84px !important;
      border-radius: 14px !important;
      padding: 9px 7px !important;
    }
    .admin-content .admin-dashboard-kpis article > div:first-child {
      font-size: 8px !important;
      line-height: 1.05 !important;
      gap: 4px !important;
    }
    .admin-content .admin-dashboard-kpis article > strong {
      margin-top: 9px !important;
      font-size: 16px !important;
      line-height: 1 !important;
      white-space: nowrap;
    }
    .admin-content .admin-dashboard-kpis article > div:last-child {
      margin-top: 8px !important;
      font-size: 8px !important;
      gap: 4px !important;
    }
    .admin-content .admin-dashboard-minis {
      display: grid !important;
      grid-template-columns: repeat(3, minmax(0, 1fr)) !important;
      gap: 8px !important;
    }
    .admin-content .admin-dashboard-minis article {
      min-height: 72px !important;
      padding: 8px 6px !important;
      border-radius: 13px !important;
      gap: 4px !important;
      justify-items: center;
      text-align: center;
    }
    .admin-content .admin-dashboard-minis article strong {
      font-size: 13px !important;
      line-height: 1 !important;
    }
    .admin-content .admin-dashboard-minis article span {
      font-size: 9px !important;
      line-height: 1.1 !important;
    }
  }
`;

export default AdminLayout;
